import moment from 'moment';
import { AFFORDABLE_PROGRAMS } from '../../utils/affordable';
import {
  NO_PREFERENCE_OPTION,
  NO_PREFERENCE_UUID,
} from '../../utils/noPreference';

/**
 * Checks if a Market Rate unit assignment should be blocked due to NAUR
 * Only Market Rate units are affected - Affordable units can be assigned freely
 *
 * @param {boolean} isFloorPlanAffordable - Is the unit affordable?
 * @param {Object} unitNaurData - NAUR data from useUnitNaurWarning hook
 * @returns {boolean} - True if assignment should be blocked
 */
export const checkNaurAssignmentRestriction = ({
  isFloorPlanAffordable,
  unitNaurData,
}) => {
  // Only applies to Market Rate units
  if (isFloorPlanAffordable) return false;

  // If unit is impacted by NAUR, block assignment
  // Backend already validated:
  // - There are OPEN events in the building
  // - Unit bedroom count <= NAUR event bedroom count
  return unitNaurData?.isImpactedByNaur || false;
};

/**
 * Checks if a unit assignment should be blocked because the household's
 * preferred floor plan is not the unit's floor plan
 * "No Preference" is a sentinel id, not a FloorPlan row, so it states no
 * constraint and is treated like an absent preference - same as the backend,
 * which drops any id it cannot resolve to a floor plan before comparing
 *
 * @param {boolean} restrictionsEnabled - Affordable Waitlist restrictions apply
 *   (Waitlist V2 rollout on AND the property runs an affordable waitlist)
 * @param {string} preferredFloorPlanId - ProspectPreference.preferredFloorPlanId
 * @param {string} unitFloorPlanId - The unit's floorPlanId
 * @returns {boolean} - True if assignment should be blocked
 */
export const checkPreferredFloorPlanRestriction = ({
  restrictionsEnabled,
  preferredFloorPlanId,
  unitFloorPlanId,
}) =>
  !!restrictionsEnabled &&
  !!preferredFloorPlanId &&
  preferredFloorPlanId !== NO_PREFERENCE_UUID &&
  preferredFloorPlanId !== unitFloorPlanId;

/**
 * The row's `moveOutDate` comes from `getUnitMoveOutDate` (AllUnitAvailability/utils.js) as an
 * 'MM/DD/YYYY' string, or null when the unit has never been occupied. Normalized here so the
 * comparison never depends on that formatting choice.
 *
 * @param {moment|string|null} value
 * @returns {moment|null}
 */
export const normalizeUnitDate = (value) => {
  if (!value) return null;
  if (moment.isMoment(value)) {
    return value.isValid() ? value : null;
  }
  if (typeof value !== 'string') return null;
  const parsed = moment(value, ['MM/DD/YYYY', 'YYYY-MM-DD'], true);
  return parsed.isValid() ? parsed : null;
};

/**
 * ENG-30316: a household cannot transfer into a unit that the prior household still occupies.
 * The earliest valid Transfer Date is the DAY AFTER the prior household's move-out date, so the
 * unit is restricted whenever the Transfer Date falls on or before that move-out.
 *
 * Keyed off the move-out date rather than the unit's move-in ready date on purpose: move-in
 * readiness clamps anything in the past to today, which would block every backdated transfer even
 * when the prior household left years ago. Move-out carries no clamp, so backdating stays possible
 * as long as the date clears the prior occupancy.
 *
 * A unit with no known prior occupancy is never restricted by this rule — those units are governed
 * by the pre-existing assignment rules.
 *
 * @returns {boolean} true when Assign must be disabled for this unit
 */
export const checkTransferDateOverlapsPriorOccupancy = ({
  isTransfer,
  transferDate,
  moveOutDate,
}) => {
  if (!isTransfer || !transferDate) return false;

  const priorMoveOut = normalizeUnitDate(moveOutDate);
  if (!priorMoveOut) return false;

  const selected = moment(transferDate, 'YYYY-MM-DD', true);
  if (!selected.isValid()) return false;

  return selected.isSameOrBefore(priorMoveOut, 'day');
};

/**
 * ENG-6289: lifted verbatim out of getIsUnitAssignDisabled so the tooltip chain can ask the same
 * question. `disabled` is threaded in and returned because the two LIHTC branches ASSIGN to it —
 * they can reset an incoming `true` to `false`. That is pre-existing behavior and is pinned by
 * test; do not convert these to `||=`.
 */
/**
 * ENG-6289: HUD contract numbers arrive from the backend already resolved, but never assume the
 * shape. Coerce to string so a numeric contract number cannot throw on .trim(), and treat a
 * trimmed-empty value as "unknown" — which never blocks an assignment (ticket Sc3).
 */
export const normalizeContractNumber = (value) => String(value ?? '').trim();

export const resolveAffordableProgramDisabled = ({
  fpPap,
  transferFpPap,
  disabled,
}) => {
  let result = disabled;

  /*
   * You cannot transfer between different set of affordable programs
   * HUD can only transfer to other HUD units.
   * RD can only transfer to other RD units.
   * PH can only transfer to other PH units,
   * As of 2026, all programs that are not HUD, RD or PH are considered LIHTC flavors
   * so transfers between these program's units are allowed.
   * Ex. HOME, NOAH, VHCB and so on are technically LIHTC with different names.
   */
  const currentHasHUD = transferFpPap.some(
    (fpap) => fpap?.pap?.map?.name === AFFORDABLE_PROGRAMS.HUD,
  );
  const currentHasRD = transferFpPap.some(
    (fpap) => fpap?.pap?.map?.name === AFFORDABLE_PROGRAMS.RD,
  );
  const currentHasPH = transferFpPap.some(
    (fpap) => fpap?.pap?.map?.name === AFFORDABLE_PROGRAMS.PH,
  );
  const targetHasHUD = fpPap.some(
    (fpap) => fpap?.pap?.map?.name === AFFORDABLE_PROGRAMS.HUD,
  );
  const targetHasRD = fpPap.some(
    (fpap) => fpap?.pap?.map?.name === AFFORDABLE_PROGRAMS.RD,
  );
  const targetHasPH = fpPap.some(
    (fpap) => fpap?.pap?.map?.name === AFFORDABLE_PROGRAMS.PH,
  );

  const nonLIHTCPrograms = [
    AFFORDABLE_PROGRAMS.HUD,
    AFFORDABLE_PROGRAMS.RD,
    AFFORDABLE_PROGRAMS.PH,
  ];

  const nonLIHTCConditionals = {
    fromHUD: currentHasHUD && !targetHasHUD,
    fromRD: currentHasRD && !targetHasRD,
    fromPH: currentHasPH && !targetHasPH,
    toHUD: targetHasHUD && !currentHasHUD,
    toRD: targetHasRD && !currentHasRD,
    toPH: targetHasPH && !currentHasPH,
  };

  // To transfer from or to HUD, RD or PH; both units must have the same program
  if (Object.values(nonLIHTCConditionals).some((condition) => condition)) {
    result = true;
  }

  const targetLIHTCPrograms =
    (fpPap ?? [])
      .filter((fpap) => !nonLIHTCPrograms.includes(fpap?.pap?.map?.name))
      .map((fpap) => fpap.propertyAffordableProgramId) ?? [];
  const currentLIHTCPrograms =
    (transferFpPap ?? [])
      .filter((fpap) => !nonLIHTCPrograms.includes(fpap?.pap?.map?.name))
      .map((fpap) => fpap.propertyAffordableProgramId) ?? [];

  /*
   * If the unit I want to transfer has a LIHTC like program but my current unit does not have any LIHTC like programs
   * it means that I currently live in an unit with either HUD, RD or PH; so the transfer can only be allowed
   * if the target unit has the same type of program on it.
   */
  if (targetLIHTCPrograms.length > 0 && currentLIHTCPrograms.length === 0) {
    result =
      nonLIHTCConditionals.fromHUD ||
      nonLIHTCConditionals.fromRD ||
      nonLIHTCConditionals.fromPH;
  }

  /*
   * If the unit I want to transfer doesn't have a LIHTC like pogram but my current unit does;
   * I have to check if I'm on a HUD/RD, or PH/RD combo unit, if I am then the transfer can only be allowed
   * if the target unit has the same HUD, RD, or PH program on it; otherwise, it means I want to transfer
   * to a conventional unit which is not allowed.
   */
  if (targetLIHTCPrograms.length === 0 && currentLIHTCPrograms.length > 0) {
    result =
      nonLIHTCConditionals.toHUD ||
      nonLIHTCConditionals.toRD ||
      nonLIHTCConditionals.toPH;
  }

  return result;
};

/**
 * ENG-6289: "is this row blocked *because of* affordable programs?" — the same rule evaluated
 * from a clean slate, for the tooltip chain.
 */
export const checkAffordableProgramMismatch = ({ fpPap, transferFpPap }) =>
  resolveAffordableProgramDisabled({ fpPap, transferFpPap, disabled: false });

export const getIsUnitAssignDisabled = ({
  assignedApplicationId,
  applicantToAssignUnitId,
  applicantApplicationStatus,
  applicantPropertyClass,
  isFloorPlanAffordable,
  isFloorPlanCommercial,
  isSelfAssigned,
  applicationIsCommercial,
  isTransfer,
  isCurrentUnitConventional,
  isNotSameHUDTypeFloorplan,
  hasMultipleHUDSubsidies,
  lastCertIsTermination,
  isDifferentHudContract,
  singleBuildingElection,
  fromBuilding,
  unitBuilding,
  fpPap,
  transferFpPap,
  isCurrentUnitLIHTC,
  unitNaurData,
  flags,
  waitlistRestrictionsEnabled,
  preferredFloorPlanId,
  originalUnit,
  transferDate,
  moveOutDate,
}) => {
  const canAssignUnit = [
    'Approved',
    'In Process',
    'Converted to Resident',
  ].includes(applicantApplicationStatus?.name);

  const applicationMatchesPropertyClass = checkApplicationMatchesPropertyClass({
    applicationPropertyClass: applicantPropertyClass?.name,
    isFloorPlanAffordable,
    isFloorPlanCommercial,
    applicationIsCommercial,
  });

  let disabled = false;

  if (!canAssignUnit || isSelfAssigned || !applicationMatchesPropertyClass)
    disabled = true;

  if (
    applicantToAssignUnitId &&
    assignedApplicationId &&
    applicantToAssignUnitId !== assignedApplicationId
  ) {
    disabled = true;
  }

  if (isTransfer) {
    // You cannot transfer from a conventional unit to an affordable unit
    if (isCurrentUnitConventional && isFloorPlanAffordable) {
      disabled = true;
    }
    // You cannot transfer from an affordable unit to a conventional unit
    if (!isCurrentUnitConventional && !isFloorPlanAffordable) {
      disabled = true;
    }

    if (
      isCurrentUnitLIHTC &&
      singleBuildingElection &&
      fromBuilding !== unitBuilding
    ) {
      disabled = true;
    }

    if (
      isNotSameHUDTypeFloorplan &&
      hasMultipleHUDSubsidies &&
      !lastCertIsTermination
    ) {
      disabled = true;
    }

    // ENG-30316: the prior household still occupies the unit on the selected Transfer Date.
    if (
      checkTransferDateOverlapsPriorOccupancy({
        isTransfer,
        transferDate,
        moveOutDate,
      })
    ) {
      disabled = true;
    }

    disabled = resolveAffordableProgramDisabled({
      fpPap,
      transferFpPap,
      disabled,
    });

    /*
     * ENG-6289: a household cannot transfer into a unit on a different HUD contract number.
     * The resolved comparison (explicit contract link, else the property's primary HUD contract,
     * and the "either side empty => allow" rule) happens on the backend; this is a plain boolean.
     *
     * MUST stay the last rule in this block: the two LIHTC refinements above ASSIGN to `disabled`
     * rather than OR into it, so any rule placed before them can be silently reset to false. A
     * household on a HUD-only floor plan assigning a layered HUD+LIHTC unit hits exactly that path.
     */
    if (flags?.unitTransfers && isDifferentHudContract) {
      disabled = true;
    }
  }

  // NAUR Assignment Restriction (Market Rate units only)
  if (
    flags?.naur &&
    checkNaurAssignmentRestriction({
      isFloorPlanAffordable,
      unitNaurData,
    })
  ) {
    disabled = true;
  }

  if (
    checkPreferredFloorPlanRestriction({
      restrictionsEnabled: waitlistRestrictionsEnabled,
      preferredFloorPlanId,
      unitFloorPlanId: originalUnit?.floorPlanId,
    })
  ) {
    disabled = true;
  }

  return disabled;
};

export const checkShouldRenderAssignOption = ({
  applicationId,
  assignedApplicationId,
}) => {
  return (
    !!applicationId &&
    (!assignedApplicationId ||
      (assignedApplicationId && assignedApplicationId !== applicationId))
  );
};

export const checkApplicationMatchesPropertyClass = ({
  applicationPropertyClass,
  isFloorPlanAffordable,
  isFloorPlanCommercial,
  applicationIsCommercial,
}) => {
  const isApplicationAffordable = applicationPropertyClass === 'Affordable';
  const applicationIsAffordableAndMatchesAffordablePropertyClass =
    isApplicationAffordable &&
    isApplicationAffordable === isFloorPlanAffordable;

  const applicationIsCommercialAndMatchesCommercialPropertyClass =
    applicationIsCommercial &&
    applicationIsCommercial === isFloorPlanCommercial;

  const applicationIsConventional = applicationPropertyClass === 'Conventional';
  const applicationIsConventionalAndMatchesConventionalPropertyClass =
    applicationIsConventional &&
    applicationIsConventional ===
      (!isFloorPlanCommercial &&
        !isFloorPlanAffordable &&
        !isApplicationAffordable &&
        !applicationIsCommercial);

  const applicationMatchesPropertyClass =
    applicationIsAffordableAndMatchesAffordablePropertyClass ||
    applicationIsCommercialAndMatchesCommercialPropertyClass ||
    applicationIsConventionalAndMatchesConventionalPropertyClass;

  return applicationMatchesPropertyClass;
};

// Function to sanitize text for CSV export
export const sanitizeForCSV = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text
    .replace(/[\r\n]+/g, ' ') // Replace line breaks with spaces
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/[",]/g, '') // Remove commas and quotes that break CSV
    .trim(); // Remove leading/trailing whitespace
};

// Whether the applicant's own floor plan narrows the unit list. Product has not
// confirmed whether this should apply only on waitlist properties; if it should,
// add `&& waitlistConfiguration?.enabled` here and pass the configuration in.
export const shouldRestrictByFloorPlan = ({ preferredFloorPlanId }) =>
  Boolean(preferredFloorPlanId) &&
  preferredFloorPlanId !== NO_PREFERENCE_OPTION.value;

// The application is continuing for a non-waitlist program. Only the cancel
// flow reaches this state: it offers "continue application" and leaves the
// application In Process, while denying ends the application outright.
export const shouldRestrictToNonWaitlistPrograms = ({
  waitlistPositions,
  applicationStatusName,
}) =>
  applicationStatusName === 'In Process' &&
  waitlistPositions.length > 0 &&
  waitlistPositions.every(({ status }) => status === 'CANCELLED');

// A floor plan can carry several programs at once — LIHTC layered with a HUD
// contract is common — and only some of them may run a waitlist. The unit is
// out of reach for an applicant continuing outside the waitlist only when it
// offers no non-waitlist program to house them under; if even one of its
// programs is off the waitlist, they can be certified under that one.
// Conventional floor plans carry no programs at all and are always available.
export const isWaitlistOnlyUnit = ({ fpPap, waitlistProgramNames }) =>
  fpPap.length > 0 &&
  fpPap.every(({ pap }) => waitlistProgramNames.includes(pap?.map?.name));
